import React, { Fragment, useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { Box, Popper, ClickAwayListener } from '@material-ui/core'

import { Button } from '@components/Button'
import { Tooltip } from '@components/Tooltip'

import { Card } from '@components/layout/Card'
import { TalkToAWealthManagerModal } from '@components/Modal/TalkToAWealthManagerModal'
import AstuteRequestConfirmProceedModal from '@components/Modal/AstuteRequestConfirmProceedModal'
import ConfigurePdfDownloadModal from '@components/Modal/ConfigurePdfDownloadModal'
import Http from '@shared/http'
import routes from '@utils/routes'
import { TrackingEvents } from '@utils/tracking'
import useFeatureConfig, { FeatureNames } from '@hooks/useFeatureConfig'
import useModalVisibilityControls from '@hooks/useModalVisibilityControls'
import { useCreateInvestmentProposal } from '@/containers/Scenario/components/CreateInvestmentProposal'

import { ImplementScenarioModal } from './ImplementScenarioModal'
import { ArchiveScenarioModal } from './ArchiveScenarioModal'
import './plan-actions.scss'

export const PlanActions = (props) => {
  const { menuAnchorElement, toggleMenu, closeMenu, menuVisible, menuId } = usePlanActionsDropdownMenu()
  const [primaryAction, ...additionalActions] = buildPlanActions(props)

  if (!primaryAction) {
    return null
  }

  return (
    <Card noBorder className='plan-actions'>
      <div className='dropdown-button'>
        {primaryAction.render({ buttonType: 'primary' })}

        {additionalActions.length > 0 ? (
          <MenuTrigger
            aria-describedby={menuId}
            open={menuVisible}
            onClick={toggleMenu} />
        ) : null}
      </div>

      {additionalActions.length > 0 ? (
        <Fragment>
          <Popper
            className='plan-actions__popover-menu'
            id={menuId}
            open={menuVisible}
            anchorEl={menuAnchorElement}>
            <ClickAwayListener onClickAway={closeMenu}>
              <Card className='plan-actions__popover-menu__content'>
                {additionalActions.map(action => action.render({ buttonType: 'secondary' }))}
              </Card>
            </ClickAwayListener>
          </Popper>

          <style>{`
            .plan-actions__popover-menu {
              top: 0.5rem !important;
            }

            .plan-actions__popover-menu__content {
              display: flex;
              flex-direction: column;
              padding: 1rem;
              gap: 1rem;
            }
          `}
          </style>
        </Fragment>) : null}
    </Card>
  )
}

const usePlanActionsDropdownMenu = () => {
  const [menuAnchorElement, setMenuAnchorElement] = React.useState()
  const toggleMenu = (event) => {
    setMenuAnchorElement(menuAnchorElement ? null : event.currentTarget)
  }
  const closeMenu = (event) => {
    event && event.preventDefault()
    setMenuAnchorElement(null)
  }
  const menuVisible = Boolean(menuAnchorElement)
  const menuId = open ? 'plan-actions-popover-menu' : undefined

  return {
    menuAnchorElement,
    toggleMenu,
    closeMenu,
    menuVisible,
    menuId
  }
}

const MenuTrigger = ({ open, onClick, ...passThroughProps }) => {
  const iconClass = classNames('dropdown-arrow', {
    'dropdown-menu-open': open
  })

  return (
    <Button
      className='button plan-actions__menu-trigger'
      style={{ maxWidth: '3rem' }}
      trackAs='Scenario: Dropdown actions'
      onClick={onClick}
      secondary
      {...passThroughProps}>
      <span className={iconClass} />
    </Button>
  )
}

const redirectOnUpdateScenario = (updateScenarioCallback) => async (payload) => {
  const { redirectPath } = await updateScenarioCallback(payload)
  if (redirectPath) {
    window.location = redirectPath
  }
}

const ImplementScenarioOrRequestReviewAction = ({
  updateScenario, usePresentValue, scenario, riskProfile, comparedRiskProfile,
  otpPaths, clientCellphoneNumber, isClient, buttonType, editClientPath,
  clientHasIdentificationNumber, jointScenarioId
}) => {
  const { modalVisible, openModal, closeModal } = useModalVisibilityControls()
  let button = jointScenarioId ? (
    <Tooltip title="Scenarios belonging to clients associated with a joint plan can't be implemented">
      <Button
        {...{ [buttonType]: true }}
        disabled
        onClick={(e) => e.preventDefault}>
        Implement scenario
      </Button>
    </Tooltip>
  ) : (
    <Button
      {...{ [buttonType]: true }}
      onClick={openModal}>
      Implement scenario
    </Button>
  )

  return (
    <Fragment>
      {button}

      <ImplementScenarioModal
        open={modalVisible}
        onClose={closeModal}
        scenario={scenario}
        riskProfile={riskProfile}
        comparedRiskProfile={comparedRiskProfile}
        onUpdateScenario={redirectOnUpdateScenario(updateScenario)}
        usePresentValue={usePresentValue}
        otpPaths={otpPaths}
        editClientPath={editClientPath}
        clientHasIdentificationNumber={clientHasIdentificationNumber}
        clientCellphoneNumber={clientCellphoneNumber}
        isClient={isClient} />
    </Fragment>
  )
}

const ArchiveScenarioAction = ({ scenarioName, updateScenario }) => {
  const { modalVisible, openModal, closeModal } = useModalVisibilityControls()

  return (
    <Fragment>
      <Button warning onClick={openModal}>Archive scenario</Button>
      <ArchiveScenarioModal
        open={modalVisible}
        close={closeModal}
        onUpdateScenario={redirectOnUpdateScenario(updateScenario)}
        scenarioName={scenarioName} />
    </Fragment>
  )
}

const CreateNewScenarioAction = ({ onCreateNewScenario, buttonType }) =>
  <Button {...{ [buttonType]: true }} onClick={() => onCreateNewScenario()}>
    New scenario
  </Button>

/** This action uses a legacy scenario planning backend endpoint instead of the
 * API, and should be refactored in the sayg cleanups to use a API endpoint.
 */
const DownloadScenarioPDFReportAction = ({ isImplemented, scenarioId, path, usePresentValue, buttonType }) => {
  const { modalVisible, openModal, closeModal } = useModalVisibilityControls()

  return (
    <Fragment>
      <Button
        {...{ [buttonType]: true }}
        trackAs={isImplemented ? TrackingEvents.implementedPlan.downloadPDF : TrackingEvents.scenario.downloadPDF}
        onClick={openModal}>Download PDF Report</Button>

      <ConfigurePdfDownloadModal
        open={modalVisible}
        onClose={closeModal}
        scenarioId={scenarioId}
        paths={{ pdfReportPath: path }}
        usePresentValue={usePresentValue}
        isEmail={false} />
    </Fragment>
  )
}

/** This action uses a legacy scenario planning backend endpoint instead of the
 * API, and should be refactored in the sayg cleanups to use a API endpoint.
 */
const SendScenarioPDFReportAction = ({ path, usePresentValue, buttonType }) => {
  const { modalVisible, openModal, closeModal } = useModalVisibilityControls()

  return (
    <Fragment>
      <Button
        {...{ [buttonType]: true }}
        trackAs={TrackingEvents.implementedPlan.sendPDF}
        onClick={openModal}>
        Send PDF Report
      </Button>

      <ConfigurePdfDownloadModal
        open={modalVisible}
        usePresentValue={usePresentValue}
        onClose={closeModal}
        paths={{ notifyCompliancePath: path }}
        isEmail={true} />
    </Fragment>
  )
}

const CreateOrViewAstuteRequestAction = ({ existingAstuteRequest, astuteRequestsPath, buttonType }) => {
  const { modalVisible, openModal, closeModal } = useModalVisibilityControls()
  const actionLabel = existingAstuteRequest ? 'View' : 'Get'
  const trackAs = existingAstuteRequest ?
    TrackingEvents.implementedPlan.viewAstuteReport :
    TrackingEvents.implementedPlan.openGetAstuteReportModal

  return (
    <Fragment>
      <Button
        {...{ [buttonType]: true }}
        trackAs={existingAstuteRequest && trackAs}
        href={existingAstuteRequest ? astuteRequestsPath : null}
        onClick={existingAstuteRequest ? null : openModal}>
        {`${actionLabel} Astute Report`}
      </Button>

      <AstuteRequestConfirmProceedModal
        show={modalVisible}
        onClose={closeModal}
        createAstuteRequestPath={astuteRequestsPath} />
    </Fragment>
  )
}

const CreateOrViewJointPlanAction = ({
  jointScenarioId,
  path,
  permissions: { createJointPlanEnabled, viewJointPlanEnabled },
  buttonType
}) => {
  /* A user should not see the view joint plan button if they only have view
   * permission for joint plans and a joint plan does not exist */
  if (!createJointPlanEnabled && viewJointPlanEnabled && !jointScenarioId) {
    return null
  }

  const canCreate = createJointPlanEnabled && !jointScenarioId
  const actionLabel = canCreate ? 'Create' : 'View'
  const trackAs = canCreate ? (
    TrackingEvents.jointPlan.create
  ) : (
    TrackingEvents.jointPlan.view
  )

  return (
    <Button {...{ [buttonType]: true }} trackAs={trackAs} href={path}>
      {`${actionLabel} joint plan`}
    </Button>
  )
}

const TalkToAWealthManagerAction = ({ path, scenarioImplemented, buttonType }) => {
  const { modalVisible, openModal, closeModal } = useModalVisibilityControls()
  const requestWealthManager = (path) => async () => {
    return await Http.request(path, 'POST')
  }

  return (
    <Fragment>
      <Button
        {...{ [buttonType]: true }}
        trackAs='Dashboard: Talk to an advisor'
        onClick={openModal}>
        Talk to an advisor
      </Button>

      <TalkToAWealthManagerModal
        scenarioImplemented={scenarioImplemented}
        open={modalVisible}
        onClose={closeModal}
        onAccept={requestWealthManager(path)} />
    </Fragment>
  )
}

const ViewProfileAction = ({ isClient, buttonType, path }) => {
  const buttonText = `View${isClient ? ' ' : ' Client '}Profile`

  return (
    <Button
      {...{ [buttonType]: true }}
      href={path}
      trackAs='View Client Profile'>
      {buttonText}
    </Button>
  )
}

const CreateInvestmentProposalAction = ({
  buttonType, tenantSlug, clientId, profileId, riskProfileId, assets,
  scenarioPlanType
}) => {
  const createInvestmentProposal = useCreateInvestmentProposal({
    dfmIntegrationSlug: 'strategiq', tenantSlug, client_id: clientId,
    profile_id: profileId, risk_profile_id: riskProfileId, assets,
    scenarioPlanType
  })

  return (
    <Fragment>
      {createInvestmentProposal.modal}

      <Button
        {...{ [buttonType]: true }}
        onClick={createInvestmentProposal.showCreateInvestmentProposalModal}
        trackAs='Create Investment Proposal Modal'>
        Create investment proposal
      </Button>
    </Fragment>
  )
}

const buildPlanActions = ({
  scenario: {
    id: scenarioId,
    name: scenarioName,
    status: scenarioStatus,
    planType: scenarioPlanType,
  },
  assets,
  riskProfile: {
    id: riskProfileId,
    rating
  },
  comparedRiskProfile,
  tenantSlug,
  clientId,
  clientCellphoneNumber,
  clientHasIdentificationNumber,
  jointScenarioId,
  updateScenario,
  usePresentValue,
  onCreateNewScenario,
  userDetails: { profileId },

  isClient,
  existingAstuteRequest,
}) => {
  const scenarioImplemented = scenarioPlanType === 'implemented'
  const scenarioHistoric = scenarioPlanType === 'historic'
  const scenarioArchiveable = !['implemented', 'archived', 'historic'].includes(scenarioPlanType)
  const [
    createScenarioConfig,
    { isEnabled: implementScenarioEnabled },
    { isEnabled: requestScenarioReviewEnabled },
    { isEnabled: requestWealthManagerEnabled },
    { isEnabled: archiveScenarioEnabled },
    { isEnabled: downloadPDFReportEnabled },
    { isEnabled: sendPDFReportEnabled },
    { isEnabled: exportScenarioEnabled },
    { isEnabled: astuteRequestsEnabled },
    { isEnabled: createJointPlanEnabled },
    { isEnabled: viewJointPlanEnabled },
    { isEnabled: createInvestmentProposalEnabled }
  ] = useFeatureConfig(
    FeatureNames.SCENARIO__CREATE,
    FeatureNames.SCENARIO__IMPLEMENT,
    FeatureNames.SCENARIO__REQUEST_REVIEW,
    FeatureNames.WEALTH_MANAGER_REQUEST__ALL,
    FeatureNames.SCENARIO__ARCHIVE,
    FeatureNames.SCENARIO__DOWNLOAD_PDF,
    FeatureNames.SCENARIO__SEND_PDF,
    FeatureNames.SCENARIO__EXPORT_PROJECTIONS,
    FeatureNames.ASTUTE_CCP__ALL,
    FeatureNames.JOINT_PLAN__CREATE,
    FeatureNames.JOINT_PLAN__VIEW,
    FeatureNames.INVESTMENT_PROPOSAL__CREATE,
  )
  const actions = [
    {
      isEnabled: requestWealthManagerEnabled(),
      render: ({ buttonType }) => (
        <TalkToAWealthManagerAction
          key='talk-to-a-wealth-manager'
          buttonType={buttonType}
          scenarioImplemented={scenarioImplemented}
          path={routes.requestWealthManagerPath({ tenantSlug, clientId })} />
      )
    }, {
      isEnabled: (!scenarioImplemented && !scenarioHistoric && (implementScenarioEnabled() || requestScenarioReviewEnabled())),
      render: ({ buttonType }) => (
        <ImplementScenarioOrRequestReviewAction
          key='implement-scenario'
          buttonType={buttonType}
          otpPaths={{
            requestOtpPath: routes.requestOtpPath({ tenantSlug, clientId }),
            verifyOtpPath: routes.verifyOtpPath({ tenantSlug, clientId }),
            resendOtpPath: routes.resendOtpPath({ tenantSlug, clientId })
          }}
          editClientPath={routes.editClientPath({ tenantSlug, clientId })}
          clientHasIdentificationNumber={clientHasIdentificationNumber}
          clientCellphoneNumber={clientCellphoneNumber}
          isClient={isClient}
          scenario={{ name: scenarioName, status: scenarioStatus, planType: scenarioPlanType }}
          riskProfile={{
            id: riskProfileId,
            rating
          }}
          comparedRiskProfile={{
            rating: comparedRiskProfile.rating
          }}
          updateScenario={updateScenario}
          usePresentValue={usePresentValue}
          jointScenarioId={jointScenarioId} />
      )
    }, {
      isEnabled: createScenarioConfig.isEnabled() && !scenarioHistoric,
      render: ({ buttonType }) => (
        <CreateNewScenarioAction
          key='create-scenario'
          buttonType={buttonType}
          options={createScenarioConfig.getOptions()}
          onCreateNewScenario={onCreateNewScenario} />
      )
    }, {
      isEnabled: !isClient,
      render: ({ buttonType }) => (
        <ViewProfileAction
          key='view-profile-action'
          buttonType={buttonType}
          path={routes.clientProfilePath({ tenantSlug, clientId })} />
      )
    },
    {
      isEnabled: exportScenarioEnabled(),
      render: ({ buttonType }) => (
        <Button
          trackAs={scenarioImplemented ? (
            TrackingEvents.implementedPlan.exportCapitalGrowth
          ) : (
            TrackingEvents.scenario.exportCapitalGrowth
          )}
          key='export-scenario'
          {...{ [buttonType]: true }}
          href={routes.exportCapitalGrowthPath({ tenantSlug, clientId })}>
          Export projections
        </Button>
      )
    }, {
      isEnabled: (downloadPDFReportEnabled()),
      render: ({ buttonType }) => (
        <DownloadScenarioPDFReportAction
          key='download-scenario-pdf'
          buttonType={buttonType}
          isImplemented={scenarioImplemented}
          scenarioId={scenarioId}
          path={routes.downloadScenarioPDFPath({ tenantSlug, clientId })}
          usePresentValue={usePresentValue} />
      )
    }, {
      isEnabled: (scenarioImplemented && sendPDFReportEnabled()),
      render: ({ buttonType }) => (
        <SendScenarioPDFReportAction
          key='send-scenario-pdf'
          buttonType={buttonType}
          path={routes.notifyCompliancePath({ tenantSlug, clientId })}
          usePresentValue={usePresentValue} />
      )
    }, {
      isEnabled: (scenarioImplemented && astuteRequestsEnabled()),
      render: ({ buttonType }) => (
        <CreateOrViewAstuteRequestAction
          key='create-or-view-astute-request'
          buttonType={buttonType}
          existingAstuteRequest={existingAstuteRequest}
          astuteRequestsPath={routes.astuteRequestsPath({ tenantSlug, clientId })} />
      )
    }, {
      isEnabled: !isClient && (scenarioImplemented && (createJointPlanEnabled() || viewJointPlanEnabled())),
      render: ({ buttonType }) => (
        <CreateOrViewJointPlanAction
          key='joint-planning'
          buttonType={buttonType}
          jointScenarioId={jointScenarioId}
          permissions={{
            createJointPlanEnabled: createJointPlanEnabled(),
            viewJointPlanEnabled: viewJointPlanEnabled()
          }}
          isClient={isClient}
          path={
            jointScenarioId
              ? routes.jointScenarioPath({
                tenantSlug, clientId, jointScenarioId
              })
              : routes.newJointScenarioPath({
                tenantSlug, clientId
              })
          }
        />
      )
    }, {
      isEnabled: scenarioArchiveable && archiveScenarioEnabled(),
      render: () => (
        <ArchiveScenarioAction
          key='archive-scenario'
          updateScenario={updateScenario}
          scenarioName={scenarioName} />
      )
    }, {
      isEnabled: scenarioImplemented && createInvestmentProposalEnabled(),
      render: ({ buttonType }) => (
        <CreateInvestmentProposalAction
          key='create-investment-proposal'
          buttonType={buttonType}
          tenantSlug={tenantSlug}
          clientId={clientId}
          profileId={profileId}
          riskProfileId={riskProfileId}
          assets={assets}
          scenarioPlanType={scenarioPlanType} />
      )
    }
  ].filter(({ isEnabled }) => isEnabled)

  return actions
}

PlanActions.propTypes = PropTypes.exact({
  scenario: PropTypes.shape({
    name: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    planType: PropTypes.string.isRequired
  }).isRequired,
  assets: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    asset_type: PropTypes.string.isRequired,
    grouping_identifier: PropTypes.string
  })).isRequired,
  riskProfile: PropTypes.shape({
    id: PropTypes.number.isRequired,
    rating: PropTypes.number.isRequired,
  }),
  comparedRiskProfile: PropTypes.shape({
    rating: PropTypes.number.isRequired,
  }),
  tenantSlug: PropTypes.string.isRequired,
  clientId: PropTypes.number.isRequired,
  clientHasIdentificationNumber: PropTypes.bool.isRequired,
  clientCellphoneNumber: PropTypes.string.isRequired,
  updateScenario: PropTypes.func.isRequired,
  usePresentValue: PropTypes.bool.isRequired,

  onCreateNewScenario: PropTypes.func.isRequired,
  isClient: PropTypes.bool.isRequired,
  existingAstuteRequest: PropTypes.bool.isRequired,
  existingJointPlan: PropTypes.bool.isRequired,

  userDetails: PropTypes.shape({
    profileId: PropTypes.number.isRequired
  })
})
